import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { Search, Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TrackOrder = () => {
  const [phone, setPhone] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!phone) {
      toast.error('Please enter a phone number');
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await axios.get(`${API}/orders/track/${phone}`);
      setOrders(response.data);
      if (response.data.length === 0) {
        toast.info('No orders found for this phone number');
      }
    } catch (error) {
      console.error('Error tracking order:', error);
      toast.error('Failed to track order');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-[1000px] mx-auto px-6 md:px-12 py-12">
      <div className="text-center mb-12">
        <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{fontFamily: 'Playfair Display, serif'}} data-testid="track-order-title">
          Track Your Order
        </h1>
        <p className="text-gray-600">Enter your phone number to track your order</p>
      </div>

      <form onSubmit={handleSearch} className="card p-8 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter phone number"
              className="mt-2"
              data-testid="track-phone-input"
            />
          </div>
          <div className="flex items-end">
            <Button type="submit" className="bg-[#D4AF37] text-white hover:bg-[#c9a22e] w-full md:w-auto" disabled={loading} data-testid="track-search-btn">
              <Search className="mr-2" size={20} />
              {loading ? 'Searching...' : 'Track Order'}
            </Button>
          </div>
        </div>
      </form>

      {searched && orders.length === 0 && (
        <div className="text-center py-12">
          <Package size={64} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500" data-testid="no-orders-found">No orders found for this phone number</p>
        </div>
      )}

      {orders.length > 0 && (
        <div className="space-y-6">
          {orders.map((order) => (
            <div key={order.id} className="card p-6" data-testid={`tracked-order-${order.id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg">Order #{order.orderId}</h3>
                  <p className="text-gray-600 text-sm">{new Date(order.createdAt).toLocaleDateString()}</p>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                  {order.status.toUpperCase()}
                </span>
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-3">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <img src={item.image} alt={item.name} className="w-16 h-16 object-cover" />
                    <div className="flex-1">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-gray-600 text-sm">Qty: {item.quantity} × ₹{item.price}</p>
                    </div>
                    <p className="font-medium">₹{item.price * item.quantity}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 mt-4 pt-4 flex justify-between items-center">
                <div>
                  <p className="text-gray-600 text-sm">Customer: {order.customerName}</p>
                  <p className="text-gray-600 text-sm">Address: {order.address}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-600 text-sm">Total Amount</p>
                  <p className="text-[#D4AF37] font-bold text-xl">₹{order.total}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
